import { apiClient } from '../services/api';
import { BidSnapshot } from '../types/bid';
import { Project, ProjectStage, FinancePlan, HrCost, Outcome, PpeInput } from '../types/project';
import raw from './projects.json';

/**
 * 프로젝트 · 재무계획. **2026-09-29 부터 실제 DB 를 본다** — `USE_MOCK = false`.
 *
 * 공고 「프로젝트로」(`bidding/:no/take`)가 `consult_projects` 에 검토 단계로 넣고, 여기가 그것을 읽는다
 * (`GET /projects`). 그전에는 담기는 DB 에 가고 보드는 목을 읽어 **둘이 끊겨** 있었다.
 * 목으로 돌리려면 true 로 — 그때는 새로고침하면 처음으로 돌아간다.
 */
export const USE_MOCK = false;

// 목일 때만 쓴다
let projects: Project[] = JSON.parse(JSON.stringify(raw.projects));
let plans: FinancePlan[] = JSON.parse(JSON.stringify(raw.financePlans));
let nextId = Math.max(0, ...projects.map((b) => b.id)) + 1;

const delay = <T,>(v: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(v), 120));

const find = (id: number) => projects.find((b) => b.id === id)!;

const patch = (id: number, p: Partial<Project>): Promise<Project> => {
  projects = projects.map((b) => (b.id === id ? { ...b, ...p } : b));
  return delay(find(id));
};

export const projectApi = {
  list(): Promise<Project[]> {
    if (!USE_MOCK) return apiClient.get('/projects');
    return delay([...projects].sort((a, b) => a.dueAt.localeCompare(b.dueAt)));
  },

  /** 「프로젝트 추가」 — 공고 없이 검토 단계로 만든다(2026-10-07). 목에서는 쓰지 않는다 */
  create(input: { name: string; clientName: string; dueAt?: string; memo?: string }): Promise<Project> {
    return apiClient.post('/projects', input);
  },

  get(id: number): Promise<Project | undefined> {
    if (!USE_MOCK) return apiClient.get(`/projects/${id}`);
    return delay(projects.find((b) => b.id === id));
  },

  /**
   * 이미 프로젝트로 담은 공고의 키. 공고 화면은 지금 `GET /bidding/captured` 를 직접 쓴다 —
   * 이건 목 전용으로 남겨 둔다
   */
  listBidKeys(): Promise<string[]> {
    return delay(
      projects
        .filter((b) => b.bid)
        .map((b) => `${b.bid!.source}:${b.bid!.bidNtceNo}`),
    );
  },

  /** 목 전용. 실제 담기는 공고 화면이 `POST /bidding/:no/take` 로 한다 */
  addFromBid(bid: BidSnapshot, name: string): Promise<Project> {
    const dup = projects.find(
      (b) => b.bid && b.bid.source === bid.source && b.bid.bidNtceNo === bid.bidNtceNo,
    );
    if (dup) return delay(dup); // 같은 공고를 두 번 담지 않는다

    const created: Project = {
      id: nextId++,
      name,
      clientName: bid.ntceInsttNm,
      bid,
      stage: '검토',
      proposalPrice: null,
      dueAt: bid.closingAt,
      pmName: '',
      memo: '',
      outcome: null,
      decidedAt: null,
      contractAmount: null,
      ppeRegisteredAt: null,
      createdAt: new Date().toISOString(),
    };
    projects = [...projects, created];
    return delay(created);
  },

  /** 보드에서 카드를 옮겼을 때, 또는 상세의 「다음 단계로」. 수주 판정 전에는 수행으로 못 간다 — 서버가 409 로 막는다 */
  moveStage(id: number, stage: ProjectStage): Promise<Project> {
    if (!USE_MOCK) return apiClient.patch(`/projects/${id}/stage`, { stage });
    const b = find(id);
    if (stage === '수행' && b.outcome !== '수주') return delay(b);
    return patch(id, { stage });
  },

  updateMemo(id: number, memo: string): Promise<Project> {
    if (!USE_MOCK) return apiClient.patch(`/projects/${id}/memo`, { memo });
    return patch(id, { memo });
  },

  /**
   * 수주 판정. **여기서는 admin 을 건드리지 않는다** — 반영은 따로 누른다.
   * 실주하면 종료로 간다.
   */
  decide(id: number, outcome: Outcome, contractAmount: number | null): Promise<Project> {
    if (!USE_MOCK) return apiClient.post(`/projects/${id}/decide`, { outcome, contractAmount });
    return patch(id, {
      outcome,
      decidedAt: new Date().toISOString(),
      contractAmount: outcome === '수주' ? contractAmount : null,
      stage: outcome === '수주' ? '수행' : '종료',
    });
  },

  /**
   * admin PPE 반영 — 사람이 버튼을 눌렀을 때만 부른다. 수주 전에도 된다(2026-09-28).
   *
   * `POST /api/projects/:id/ppe`(`admin-link`) — 백엔드가 admin 전용 엔드포인트를 불러
   * projects·project_ppe 를 만든다. 직접 INSERT 는 하지 않는다 — admin 서비스가 PPE 초기값을 채우고 트리거가 따라 돈다.
   */
  /** admin 반영 — 새로 만들면 재무계획 숫자로 PPE 도 채운다. `ppe` 는 채우기 결과(실패해도 프로젝트는 생긴다) */
  async registerPpe(id: number, input: PpeInput): Promise<{ ppe?: { filled: boolean; note?: string } }> {
    if (!USE_MOCK) return apiClient.post(`/projects/${id}/ppe`, input);
    const b = find(id);
    if (b.outcome !== '실주' && !b.ppeRegisteredAt) await patch(id, { ppeRegisteredAt: new Date().toISOString() });
    return {};
  },

  /** 재무계획. 아직 안 잡은 프로젝트는 빈 계획을 돌려준다 */
  getPlan(projectId: number): Promise<FinancePlan> {
    if (!USE_MOCK) return apiClient.get(`/projects/${projectId}/plan`);
    const found = plans.find((p) => p.projectId === projectId);
    if (found) return delay(JSON.parse(JSON.stringify(found)));
    return delay({
      projectId,
      manpower: [],
      expenses: { travel: 0, outsourcing: 0, etc: 0 },
      proposalPrice: 0,
      status: '임시저장',
      confirmedAt: null,
      ppe: null,
    });
  },

  savePlan(plan: FinancePlan): Promise<FinancePlan> {
    if (!USE_MOCK) {
      return apiClient.put(`/projects/${plan.projectId}/plan`, plan.ppe ? { ppe: plan.ppe, proposalPrice: plan.proposalPrice } : {
        manpower: plan.manpower, expenses: plan.expenses, proposalPrice: plan.proposalPrice,
      });
    }
    plans = plans.some((p) => p.projectId === plan.projectId)
      ? plans.map((p) => (p.projectId === plan.projectId ? plan : p))
      : [...plans, plan];
    return delay(plan);
  },

  /**
   * 재무계획 확정 — 제안가를 굳히고 제안서 단계로 넘긴다.
   * **admin 에는 가지 않는다.** 실주할 수도 있는 원가안이다.
   */
  /** 나라장터 결과 지금 확인 — 낙찰이 나왔으면 수주/실주를 기록하고, 수주면 PPE 를 자동 반영한다 */
  checkBidResult(id: number): Promise<unknown> {
    return apiClient.post(`/projects/${id}/bid-result/check`);
  },

  /** 이미 이어진 admin 프로젝트의 빈 PPE 를 재무계획 숫자로 채운다(admin 이 이미 쓴 PPE 면 409) */
  fillPpe(id: number): Promise<{ ppe: { filled: boolean } }> {
    return apiClient.post(`/projects/${id}/ppe/fill`);
  },

  /** 인력원가(만원/MM) — admin PPE 와 같은 사람 · 같은 값. 연도별 */
  hrCosts(year: number): Promise<HrCost[]> {
    return apiClient.get(`/projects/plan/hr-costs?year=${year}`);
  },

  /** 확정 해제 — 판정 전에만. 서버가 제안가 사본을 지우고 임시저장으로 되돌린다 */
  unconfirmPlan(projectId: number): Promise<FinancePlan> {
    return apiClient.post(`/projects/${projectId}/plan/unconfirm`);
  },

  confirmPlan(projectId: number): Promise<FinancePlan> {
    if (!USE_MOCK) return apiClient.post(`/projects/${projectId}/plan/confirm`);
    const now = new Date().toISOString();
    plans = plans.map((p) =>
      p.projectId === projectId ? { ...p, status: '확정' as const, confirmedAt: now } : p,
    );
    const plan = plans.find((p) => p.projectId === projectId)!;
    projects = projects.map((b) =>
      b.id === projectId && b.stage === '재무계획'
        ? { ...b, stage: '제안서' as const, proposalPrice: plan.proposalPrice }
        : b,
    );
    return delay(plan);
  },
};
